import React, { useEffect, useRef, useState } from "react";
import "../components/services.css";

import autorepair from "../resources/auto-repair.png";
import autotune from "../resources/autotune.png";
import preventive from "../resources/preventive.png";
import brake from "../resources/brake.png";
import engine from "../resources/engine.png";
import inspection from "../resources/inspection.png";
import LogoSlider from "./LogoSlider";
import { Link } from "react-router-dom";

function Services() {
  const services = [
    {
      name: "Auto Repair",
      description:
        "Our expert technicians provide comprehensive auto repair services to address any issues with your vehicle. From engine repairs to electrical diagnostics, we've got you covered.",
      img: autorepair,
    },
    {
      name: "Auto Tune Ups",
      description:
        "Keep your vehicle running smoothly with our professional auto tune-up services. We'll inspect and optimize your engine, fluids, and components to ensure peak performance and fuel efficiency.",
      img: autotune,
    },
    {
      name: "Preventive Maintenance",
      description:
        "Stay ahead of costly repairs with our preventive maintenance services. We'll perform regular inspections and maintenance tasks to keep your vehicle in top condition and minimize the risk of breakdowns.",
      img: preventive,
    },
    {
      name: "Vehicle Inspection",
      description:
        "Ensure your vehicle meets safety and performance standards with our thorough vehicle inspection services. Our certified technicians will inspect all major components and provide detailed reports.",
      img: inspection,
    },
    {
      name: "Engine Diagnostics",
      description:
        "Identify and resolve engine problems quickly with our advanced engine diagnostics services. Using state-of-the-art equipment, we'll pinpoint issues and recommend the best course of action for repairs.",
      img: engine,
    },
    {
      name: "Brake Service",
      description:
        "Maintain optimal stopping power and safety with our professional brake services. We'll inspect, repair, and replace brake components as needed to ensure your vehicle stops smoothly and reliably.",
      img: brake,
    },
  ];

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const cardRefs = useRef([]);
  const ratiosRef = useRef({});
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return undefined;
    }

    const mql = window.matchMedia("(max-width: 767px)");
    let observer = null;

    const attach = () => {
      if (!mql.matches) {
        setActiveIdx(null);
        ratiosRef.current = {};
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const idx = Number(entry.target.dataset.idx);
            ratiosRef.current[idx] = entry.intersectionRatio;
          });

          let bestIdx = null;
          let bestRatio = 0.55;
          Object.entries(ratiosRef.current).forEach(([idx, ratio]) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              bestIdx = Number(idx);
            }
          });
          setActiveIdx(bestIdx);
        },
        { threshold: [0, 0.25, 0.5, 0.6, 0.75, 1] }
      );

      cardRefs.current.forEach((el) => {
        if (el) observer.observe(el);
      });
    };

    attach();

    const onMediaChange = () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      attach();
    };
    mql.addEventListener("change", onMediaChange);

    return () => {
      if (observer) observer.disconnect();
      mql.removeEventListener("change", onMediaChange);
    };
  }, []);

  return (
    <section className="relative py-24 md:py-32 bg-gradient-to-b from-white to-zinc-50 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 text-brand-700 text-xs font-semibold uppercase tracking-[0.2em] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-500"></span>
            Our Expertise
          </div>
          <h2 className="font-chivo text-4xl md:text-6xl font-black text-zinc-900 leading-tight tracking-tight">
            Bini Autorepair INC
            <br />
            <span className="gradient-text">in New York</span>
          </h2>
          <p className="mt-8 text-base md:text-lg text-zinc-600 font-montserrat leading-relaxed">
            <span className="font-bold text-zinc-900">Bini Autorepair INC</span>{" "}
            is a trusted auto repair shop located in New York City. With over 25
            years of experience, we have been serving the community with
            excellence and dedication. Our team of skilled technicians is
            proficient in handling any kind of car and tackling a wide range of
            problems. From minor repairs to major overhauls, including engine
            replacements, we are equipped to handle it all. Trust us to keep
            your vehicle running smoothly and safely on the road.
          </p>
        </div>

        {/* Service grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service, idx) => (
            <Link
              ref={(el) => (cardRefs.current[idx] = el)}
              data-idx={idx}
              to="/services"
              key={service.name}
              onMouseMove={handleMouseMove}
              className={`service-card group relative p-8 rounded-3xl bg-white border border-zinc-200/80 shadow-soft block text-left ${
                activeIdx === idx ? "is-active" : ""
              }`}
            >
              {/* Icon */}
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-800 p-3 shadow-glow flex items-center justify-center mb-6 group-hover:scale-110 group-[.is-active]:scale-110 transition-transform">
                <img
                  alt=""
                  src={service.img}
                  className="w-10 h-10 object-contain invert brightness-0"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </div>

              {/* Number badge */}
              <span className="absolute top-6 right-6 text-zinc-200 text-5xl font-black font-chivo select-none">
                {String(idx + 1).padStart(2, "0")}
              </span>

              <h3 className="font-chivo font-bold text-xl md:text-2xl text-zinc-900 mb-3">
                {service.name}
              </h3>
              <p className="font-montserrat text-sm md:text-base text-zinc-600 leading-relaxed">
                {service.description}
              </p>

              <div className="mt-6 flex items-center gap-2 text-brand-600 font-semibold text-sm opacity-0 group-hover:opacity-100 group-[.is-active]:opacity-100 -translate-x-2 group-hover:translate-x-0 group-[.is-active]:translate-x-0 transition-all">
                Learn more
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 flex justify-center">
          <Link to={"/services"}>
            <button className="group relative overflow-hidden rounded-full px-10 py-4 bg-zinc-900 text-white font-chivo font-semibold hover:bg-gradient-to-r hover:from-brand-500 hover:to-brand-700 transition-all hover:scale-[1.03] shadow-xl">
              <span className="relative z-10 flex items-center gap-3">
                Explore our services
                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
              </span>
            </button>
          </Link>
        </div>

        {/* Logo slider */}
        <div className="mt-24 pt-16 border-t border-zinc-200">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-zinc-500 mb-10">
            We service all major brands
          </p>
          <LogoSlider />
        </div>
      </div>
    </section>
  );
}

export default Services;
